import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plug, CheckCircle2, XCircle } from "lucide-react";
import { INPUT, BTN_PRIMARY, BTN_OUTLINE, Toggle, Field } from "components/admin/AdminUI";
import api from "lib/api";

const EMPTY = {
  name: "default",
  base_url: "https://opensearch:9200",
  username: "",
  password: "",
  verify_ssl: false,
  index_dns: "obs_dns_*",
  index_service: "obs_service_*",
  index_cert: "obs_cert_*",
  index_ref_cert: "ref_cert",
  index_ref_asn: "ref_asn_*",
  index_obs_host: "obs_host",
  is_active: true,
};

const INDEX_FIELDS = [
  ["index_dns", "Índice DNS"],
  ["index_service", "Índice Serviços"],
  ["index_cert", "Índice Cert (obs)"],
  ["index_ref_cert", "Índice Cert (ref)"],
  ["index_ref_asn", "Índice ASN (ref)"],
  ["index_obs_host", "Índice Host (obs)"],
];

export default function OpenSearchForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [error, setError] = useState("");
  const [testState, setTestState] = useState(null); // "testing"|"ok"|"fail"

  useEffect(() => {
    if (!isEditing) return;
    api
      .get(`/admin/opensearch/${id}/`)
      .then(({ data }) => setForm({ ...EMPTY, ...data, password: "" }))
      .catch(() => setError("Erro ao carregar conexão."))
      .finally(() => setFetching(false));
  }, [id, isEditing]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = { ...form };
      if (isEditing && !payload.password) delete payload.password;
      if (isEditing) await api.put(`/admin/opensearch/${id}/`, payload);
      else await api.post("/admin/opensearch/", payload);
      navigate("/admin/opensearch");
    } catch (err) {
      const detail = err.response?.data;
      setError(typeof detail === "object" ? Object.values(detail).flat().join(" ") : "Erro ao salvar.");
    } finally {
      setLoading(false);
    }
  }

  async function test() {
    setTestState("testing");
    try {
      const { data } = await api.post(`/admin/opensearch/${id}/test`);
      setTestState(data.ok ? "ok" : "fail");
    } catch {
      setTestState("fail");
    }
  }

  if (fetching) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => navigate("/admin/opensearch")}
        className="mb-4 flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar para Conexões
      </button>

      <h1 className="mb-1 text-2xl font-bold tracking-tight">{isEditing ? "Editar conexão" : "Nova conexão"}</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        {isEditing ? "Altere os dados da conexão OpenSearch." : "Cadastre uma conexão OpenSearch."}
      </p>

      <div className="w-full rounded-lg border border-border bg-card p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nome">
              <input className={INPUT} value={form.name} onChange={set("name")} required autoFocus={!isEditing} />
            </Field>
            <Field label="Endereço (base_url)">
              <input className={INPUT} value={form.base_url} onChange={set("base_url")} placeholder="https://opensearch:9200" required />
            </Field>
            <Field label="Usuário">
              <input className={INPUT} value={form.username} onChange={set("username")} />
            </Field>
            <Field label="Senha">
              <input className={INPUT} type="password" value={form.password} onChange={set("password")} placeholder={isEditing ? "deixe em branco para manter" : "senha"} />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {INDEX_FIELDS.map(([key, label]) => (
              <Field key={key} label={label}>
                <input className={INPUT} value={form[key]} onChange={set(key)} />
              </Field>
            ))}
          </div>

          <Toggle checked={form.verify_ssl} onChange={(v) => setForm((f) => ({ ...f, verify_ssl: v }))} label="Verificar SSL" description="Valida o certificado TLS do cluster." />
          <Toggle checked={form.is_active} onChange={(v) => setForm((f) => ({ ...f, is_active: v }))} label="Ativa" description="Conexão usada pelas buscas (desativa as demais)." />

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
            {isEditing && (
              <button type="button" className={BTN_OUTLINE} onClick={test}>
                <Plug className="h-4 w-4" />
                {testState === "testing" ? "Testando…" : "Testar conexão"}
                {testState === "ok" && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                {testState === "fail" && <XCircle className="h-4 w-4 text-red-500" />}
              </button>
            )}
            <div className="flex gap-3 sm:ml-auto">
              <button type="button" className={BTN_OUTLINE} onClick={() => navigate("/admin/opensearch")}>
                Cancelar
              </button>
              <button type="submit" className={BTN_PRIMARY} disabled={loading}>
                {loading ? "..." : isEditing ? "Salvar" : "Criar"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
